/** Drag-to-curve dead-zone math for the pen-tool gesture (#217 /
 *  #395). When a vertex is placed while drawing a polyline /
 *  polygon, keeping the pointer down and dragging shapes that
 *  vertex's bezier handles. A stationary click-to-place must stay
 *  a sharp corner, so the drag only commits handles once it clears
 *  a small dead-zone.
 *
 *  Pure (no paper.js) so it is unit-testable without a canvas.
 *  Returns the threshold in project-mm; the caller compares it to
 *  the drag vector's length in the same space. */

export type PointerKind = "mouse" | "pen" | "touch";

/** Screen-pixel grace zone for imprecise (pen / touch) pointers.
 *  A stylus or finger tap routinely wanders well past a dozen pixels
 *  as it makes and breaks contact — an Apple Pencil tap meant only to
 *  drop a corner easily slides 15-25 px during press-and-lift. The
 *  grace must comfortably exceed that so placing a vertex stays a
 *  sharp corner; only a clearly deliberate drag should bend a curve
 *  (#395). */
export const PEN_TOUCH_GRACE_PX = 28;

/** Historical mouse dead-zone, in project-mm. A mouse is
 *  pixel-precise so the tight 0.5 mm slop other tools use keeps a
 *  click-to-place sharp while the smallest intentional drag curves. */
export const MOUSE_DEAD_ZONE_MM = 0.5;

/**
 * Distance (project-mm) a drag must exceed before it converts a
 * just-placed vertex from a sharp corner into a bezier curve.
 *
 * Mouse pointers keep the historical tight 0.5 mm threshold. Pen
 * and touch pointers carry far more positional jitter — a tap meant
 * only to place a vertex can drift a millimetre or two and, under
 * the old mouse threshold, accidentally bend a straight line into a
 * curve (#395). Those pointers get a much wider grace zone measured
 * in *screen pixels* (divided by zoom) so the tolerance feels the
 * same at every zoom level instead of shrinking to sub-pixel slop
 * when zoomed out.
 *
 * @param pointerType  the active pointer's type
 * @param zoom         paper view zoom (project units per screen px is 1/zoom)
 */
export function dragToCurveDeadZoneMm(
  pointerType: PointerKind,
  zoom: number,
): number {
  if (pointerType === "mouse") return MOUSE_DEAD_ZONE_MM;
  const safeZoom = zoom > 0 ? zoom : 1;
  return PEN_TOUCH_GRACE_PX / safeZoom;
}
